import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Heading,
  HStack,
  IconButton,
  Input,
  NumberInput,
  NumberInputField,
  Select,
  SimpleGrid,
  Stack,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  Text,
  useToast,
  Divider,
  Textarea,
  Badge,
} from '@chakra-ui/react';
import { AddIcon, DeleteIcon } from '@chakra-ui/icons';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Tooltip,
  Legend,
  TimeSeriesScale,
} from 'chart.js';
import 'chartjs-adapter-date-fns';
import { secureFetch } from '../data/secureFetch';
const { formatCentsToDollars } = require('../store/calc-helpers');

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Tooltip,
  Legend,
  TimeSeriesScale,
);

function toISODateStr(d) {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

export default function TipAdmin() {
  const toast = useToast();

  // Config form state
  const [editingId, setEditingId] = useState(null);
  const [name, setName] = useState('');
  const [abKey, setAbKey] = useState('tip_ab');
  const [variant, setVariant] = useState('control');
  const [weight, setWeight] = useState(100);
  const [options, setOptions] = useState([
    { value: '0.09', label: '9%' },
    { value: '0.05', label: '5%' },
    { value: '0.03', label: '3%' },
    { value: 'custom', label: 'Custom' },
  ]);
  const [defaultValue, setDefaultValue] = useState('0.09');
  const [nudgeMessage, setNudgeMessage] = useState('Would you consider adding a small tip to support our platform?');
  const [nudgeCtaLabel, setNudgeCtaLabel] = useState("Yes, I'll help!");

  const [configs, setConfigs] = useState([]);
  const [loadingConfigs, setLoadingConfigs] = useState(false);

  // Stats state
  const [start, setStart] = useState(toISODateStr(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)));
  const [end, setEnd] = useState(toISODateStr(new Date()));
  const [stats, setStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);

  const loadConfigs = useCallback(async () => {
    setLoadingConfigs(true);
    try {
      const resp = await secureFetch('/api/tips/admin/configs');
      if (!resp.ok) throw new Error(resp.error || resp.message || 'Failed to load configs');
      setConfigs(resp);
    } catch (e) {
      toast({ title: 'Error', description: e.message, status: 'error' });
    } finally {
      setLoadingConfigs(false);
    }
  }, [toast]);

  const clearForm = () => {
    setEditingId(null);
    setName('');
    setAbKey('tip_ab');
    setVariant('control');
    setWeight(100);
    setOptions([
      { value: '0.09', label: '9%' },
      { value: '0.05', label: '5%' },
      { value: '0.03', label: '3%' },
      { value: 'custom', label: 'Custom' },
    ]);
    setDefaultValue('0.09');
    setNudgeMessage('Would you consider adding a small tip to support our platform?');
    setNudgeCtaLabel("Yes, I'll help!");
  };

  const onSubmit = async () => {
    try {
      const body = { name, abKey, variant, weight, options, defaultValue, nudgeMessage, nudgeCtaLabel };
      if (editingId) {
        const resp = await secureFetch(`/api/tips/admin/configs/${editingId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          data: body,
        });
        if (!resp.ok) throw new Error(resp.error || resp.message || 'Failed to update config');
        toast({ title: 'Updated', status: 'success' });
      } else {
        const resp = await secureFetch('/api/tips/admin/configs', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          data: body,
        });
        if (!resp.ok) throw new Error(resp.error || resp.message || 'Failed to create config');
        toast({ title: 'Created', status: 'success' });
      }
      clearForm();
      await loadConfigs();
    } catch (e) {
      toast({ title: 'Error', description: e.message, status: 'error' });
    }
  };

  const onActivate = async (id) => {
    try {
      const resp = await secureFetch(`/api/tips/admin/configs/${id}/activate`, { method: 'POST' });
      if (!resp.ok) throw new Error(resp.error || resp.message || 'Failed to activate');
      toast({ title: 'Activated', status: 'success' });
      await loadConfigs();
    } catch (e) {
      toast({ title: 'Error', description: e.message, status: 'error' });
    }
  };

  const onEdit = (cfg) => {
    setEditingId(cfg.id);
    setName(cfg.name || '');
    setAbKey(cfg.ab_key || 'tip_ab');
    setVariant(cfg.variant || 'control');
    setWeight(cfg.weight || 100);
    setOptions(cfg.options || []);
    setDefaultValue(cfg.default_value || '0.09');
    setNudgeMessage(cfg.nudge_message || '');
    setNudgeCtaLabel(cfg.nudge_cta_label || "Yes, I'll help!");
  };

  const addOption = () => setOptions(prev => [...prev, { value: '', label: '' }]);
  const removeOption = (i) => setOptions(prev => prev.filter((_, idx) => idx !== i));
  const updateOption = (i, field, value) => setOptions(prev => prev.map((o, idx) => idx === i ? { ...o, [field]: value } : o));

  useEffect(() => { loadConfigs(); }, [loadConfigs]);

  const loadStats = useCallback(async () => {
    setLoadingStats(true);
    try {
      const params = new URLSearchParams({ start, end });
      const resp = await secureFetch(`/api/tips/admin/stats/overview?${params.toString()}`);
      if (!resp.ok) throw new Error(resp.error || resp.message || 'Failed to load stats');
      setStats(resp);
    } catch (e) {
      toast({ title: 'Error', description: e.message, status: 'error' });
    } finally {
      setLoadingStats(false);
    }
  }, [start, end, toast]);

  useEffect(() => { loadStats(); }, [loadStats]);

  const lineData = useMemo(() => {
    if (!stats?.series) return { labels: [], datasets: [] };
    const labels = stats.series.map(s => new Date(s.day));
    return {
      labels,
      datasets: [
        {
          label: 'Donations (with tip)',
          data: stats.series.map(s => s.withTip),
          borderColor: '#FFA726',
          backgroundColor: 'rgba(255,167,38,0.2)'
        },
        {
          label: 'Donations (without tip)',
          data: stats.series.map(s => s.withoutTip),
          borderColor: '#66BB6A',
          backgroundColor: 'rgba(102,187,106,0.2)'
        }
      ]
    };
  }, [stats]);

  const donutData = useMemo(() => {
    if (!stats?.optionDistribution) return { labels: [], datasets: [] };
    const labels = stats.optionDistribution.map(o => o.option);
    const data = stats.optionDistribution.map(o => o.count);
    return {
      labels,
      datasets: [{ data, backgroundColor: ['#FFA726','#66BB6A','#29B6F6','#AB47BC','#EF5350','#FF7043'] }]
    };
  }, [stats]);

  const eventsData = useMemo(() => {
    if (!stats?.events) return { labels: [], datasets: [] };
    return {
      labels: stats.events.map(e => e.eventType),
      datasets: [
        { label: 'Events', data: stats.events.map(e => e.count), backgroundColor: '#29B6F6' }
      ]
    };
  }, [stats]);

  const variantsData = useMemo(() => {
    if (!stats?.variants) return { labels: [], datasets: [] };
    const labels = stats.variants.map(v => `${v.abKey}:${v.variant}`);
    return {
      labels,
      datasets: [
        { label: 'Donations', data: stats.variants.map(v => v.donations), backgroundColor: '#90CAF9' },
        { label: 'Avg Tip %', data: stats.variants.map(v => Math.round((v.avgTipRate || 0) * 1000)/10), backgroundColor: '#FFCC80', yAxisID: 'y1' },
      ]
    };
  }, [stats]);

  return (
    <Box>
      <Heading size="lg" mb={4}>Tips A/B Admin</Heading>

      <Box mb={8} p={4} borderWidth="1px" borderRadius="md">
        <Heading size="md" mb={3}>{editingId ? 'Edit Tip Configuration' : 'Create Tip Configuration'}</Heading>
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
          <Box>
            <Stack spacing={3}>
              <Input placeholder="Name" value={name} onChange={e => setName(e.target.value)} />
              <HStack>
                <Input placeholder="AB Key" value={abKey} onChange={e => setAbKey(e.target.value)} />
                <Input placeholder="Variant" value={variant} onChange={e => setVariant(e.target.value)} />
              </HStack>
              <NumberInput value={weight} onChange={(v) => setWeight(Number(v) || 0)} min={0} max={100}>
                <NumberInputField placeholder="Weight (0-100)" />
              </NumberInput>

              <Text fontWeight="semibold">Options</Text>
              {options.map((opt, idx) => (
                <HStack key={idx}>
                  <Input placeholder="Value (e.g., 0.09 or custom)" value={opt.value} onChange={e => updateOption(idx, 'value', e.target.value)} />
                  <Input placeholder="Label (e.g., 9%)" value={opt.label} onChange={e => updateOption(idx, 'label', e.target.value)} />
                  <IconButton aria-label="remove" icon={<DeleteIcon />} onClick={() => removeOption(idx)} />
                </HStack>
              ))}
              <Button leftIcon={<AddIcon />} onClick={addOption} variant="outline" w="fit-content">Add Option</Button>

              <Select value={defaultValue} onChange={e => setDefaultValue(e.target.value)}>
                {options.map((opt, i) => (<option key={i} value={opt.value}>{opt.label} ({opt.value})</option>))}
              </Select>
              <Textarea placeholder="Nudge message" value={nudgeMessage} onChange={e => setNudgeMessage(e.target.value)} />
              <Input placeholder="Nudge CTA Label" value={nudgeCtaLabel} onChange={e => setNudgeCtaLabel(e.target.value)} />

              <HStack>
                <Button colorScheme="orange" onClick={onSubmit}>{editingId ? 'Update' : 'Create'}</Button>
                {editingId && <Button onClick={clearForm} variant="ghost">Cancel</Button>}
              </HStack>
            </Stack>
          </Box>

          <Box>
            <Heading size="sm" mb={2}>Existing Configurations</Heading>
            {loadingConfigs && <Text fontSize="sm" color="gray.500">Loading configurations…</Text>}
            <Box overflowX="auto">
              <Table size="sm">
                <Thead>
                  <Tr>
                    <Th>Name</Th>
                    <Th>AB Key</Th>
                    <Th>Variant</Th>
                    <Th>Active</Th>
                    <Th></Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {configs.map(cfg => (
                    <Tr key={cfg.id}>
                      <Td>
                        <Stack spacing={0}>
                          <Text fontWeight="semibold">{cfg.name}</Text>
                          <Text fontSize="xs" color="gray.500">Default: {cfg.default_value}</Text>
                        </Stack>
                      </Td>
                      <Td>{cfg.ab_key || '-'}</Td>
                      <Td>{cfg.variant || '-'}</Td>
                      <Td>{cfg.active ? <Badge colorScheme="green">Active</Badge> : <Badge>Inactive</Badge>}</Td>
                      <Td>
                        <HStack>
                          <Button size="xs" onClick={() => onEdit(cfg)}>Edit</Button>
                          {!cfg.active && <Button size="xs" colorScheme="orange" onClick={() => onActivate(cfg.id)}>Activate</Button>}
                        </HStack>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          </Box>
        </SimpleGrid>
      </Box>

      <Divider mb={6} />

      <Heading size="md" mb={3}>Stats & Analysis</Heading>
      <HStack mb={4}>
        <Input type="date" value={start} onChange={e => setStart(e.target.value)} />
        <Input type="date" value={end} onChange={e => setEnd(e.target.value)} />
        <Button onClick={loadStats} isLoading={loadingStats} colorScheme="orange">Refresh</Button>
      </HStack>

      {stats && (
        <Stack spacing={6}>
          <SimpleGrid columns={{ base: 1, md: 4 }} spacing={4}>
            <StatBox label="Total Donations" value={stats.summary.totalDonations} />
            <StatBox label="Donations With Tip" value={stats.summary.donationsWithTip} />
            <StatBox label="Take Rate" value={`${Math.round((stats.summary.takeRate || 0) * 1000)/10}%`} />
            <StatBox label="Total Tips" value={`$${formatCentsToDollars(stats.summary.totalTipCents || 0)}`} />
          </SimpleGrid>

          <Box p={4} borderWidth="1px" borderRadius="md">
            <Heading size="sm" mb={2}>Donations Over Time</Heading>
            <Line
              data={lineData}
              options={{
                responsive: true,
                scales: {
                  x: { type: 'time', time: { unit: 'day' } },
                  y: { beginAtZero: true },
                }
              }}
            />
          </Box>

          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
            <Box p={4} borderWidth="1px" borderRadius="md">
              <Heading size="sm" mb={2}>Option Distribution</Heading>
              <Doughnut data={donutData} />
            </Box>
            <Box p={4} borderWidth="1px" borderRadius="md">
              <Heading size="sm" mb={2}>Tip Events</Heading>
              <Bar data={eventsData} options={{ scales: { y: { beginAtZero: true } } }} />
            </Box>
          </SimpleGrid>

          <Box p={4} borderWidth="1px" borderRadius="md">
            <Heading size="sm" mb={2}>Variant Breakdown</Heading>
            <Bar
              data={variantsData}
              options={{
                responsive: true,
                scales: {
                  y: { beginAtZero: true, position: 'left' },
                  y1: { beginAtZero: true, position: 'right', grid: { drawOnChartArea: false } },
                }
              }}
            />
          </Box>
        </Stack>
      )}
    </Box>
  );
}

function StatBox({ label, value }) {
  return (
    <Box p={4} borderWidth="1px" borderRadius="md">
      <Text fontSize="sm" color="gray.500">{label}</Text>
      <Text fontSize="2xl" fontWeight="bold">{value}</Text>
    </Box>
  );
}
